/**
 * Agent Preferences Page (MVP)
 *
 * Bids removed. Agents can only toggle lead types and caps.
 */

import { useState, useEffect, useCallback } from "react";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import LeadTypeCard from "@/components/dashboard/LeadTypeCard";
import { useAgent, LeadPreference } from "@/contexts/AgentContext";
import { useToast } from "@/hooks/use-toast";

const Preferences = () => {
  const { state, actions } = useAgent();
  const { toast } = useToast();
  const [hasChanges, setHasChanges] = useState(false);

  const [localPreferences, setLocalPreferences] = useState<LeadPreference[]>(() =>
    JSON.parse(JSON.stringify(state.preferences))
  );

  useEffect(() => {
    setLocalPreferences(JSON.parse(JSON.stringify(state.preferences)));
    setHasChanges(false);
  }, [state.preferences]);

  const toggleActive = useCallback((leadTypeId: string) => {
    setLocalPreferences((prev) =>
      prev.map((pref) =>
        pref.leadTypeId === leadTypeId ? { ...pref, isActive: !pref.isActive } : pref
      )
    );
    setHasChanges(true);
  }, []);

  const updateDailyCap = useCallback((leadTypeId: string, value: number) => {
    setLocalPreferences((prev) =>
      prev.map((pref) =>
        pref.leadTypeId === leadTypeId ? { ...pref, dailyCap: value } : pref
      )
    );
    setHasChanges(true);
  }, []);

  const updateMonthlyCap = useCallback((leadTypeId: string, value: number) => {
    setLocalPreferences((prev) =>
      prev.map((pref) =>
        pref.leadTypeId === leadTypeId ? { ...pref, monthlyCap: value } : pref
      )
    );
    setHasChanges(true);
  }, []);

  const performSave = useCallback(() => {
    actions.setPreferences(localPreferences);
    setHasChanges(false);
    toast({
      title: "Preferences saved",
      description: "Your delivery preferences have been updated.",
    });
  }, [localPreferences, actions, toast]);

  const lifeInsuranceTypes = localPreferences.filter((lt) => lt.vertical === "life_insurance");
  const mortgageTypes = localPreferences.filter((lt) => lt.vertical === "mortgage");

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Delivery Preferences</h1>
            <p className="text-sm text-muted-foreground">
              Toggle lead types and set caps. Delivery speed is managed in Delivery Settings.
            </p>
          </div>
          {hasChanges && (
            <Button className="gap-2" onClick={performSave}>
              <Save className="w-4 h-4" />
              Save Changes
            </Button>
          )}
        </div>

        <div className="space-y-6">
          {lifeInsuranceTypes.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Life Insurance</h2>
              <div className="space-y-3">
                {lifeInsuranceTypes.map((pref) => (
                  <LeadTypeCard
                    key={pref.leadTypeId}
                    preference={pref}
                    onToggleActive={() => toggleActive(pref.leadTypeId)}
                    onUpdateDailyCap={(v) => updateDailyCap(pref.leadTypeId, v)}
                    onUpdateMonthlyCap={(v) => updateMonthlyCap(pref.leadTypeId, v)}
                  />
                ))}
              </div>
            </div>
          )}

          {mortgageTypes.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Mortgage</h2>
              <div className="space-y-3">
                {mortgageTypes.map((pref) => (
                  <LeadTypeCard
                    key={pref.leadTypeId}
                    preference={pref}
                    onToggleActive={() => toggleActive(pref.leadTypeId)}
                    onUpdateDailyCap={(v) => updateDailyCap(pref.leadTypeId, v)}
                    onUpdateMonthlyCap={(v) => updateMonthlyCap(pref.leadTypeId, v)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Preferences;
